"use client";

import { useRef, type RefObject } from "react";
import type { RowData } from "@tanstack/react-table";
import { useRecordGridTable } from "./use-record-grid-table";
import { useRowMenu } from "./use-row-menu";
import { useGridScrollRestoration } from "./use-grid-scroll-restoration";
import { useRememberRecordSequence } from "./use-remember-record-sequence";
import { useContainerWidth, useGridColumnLayout } from "./use-grid-column-layout";
import { useColumnReorder } from "./use-column-reorder";
import { useHeldRows } from "./use-held-rows";
import type { RecordGridProps } from "./record-grid-props";

export type RecordGridModel<TData extends RowData> = ReturnType<
  typeof useRecordGridModel<TData>
>;

export function useRecordGridModel<TData extends RowData>(
  props: RecordGridProps<TData>,
  scrollRef: RefObject<HTMLDivElement | null>,
) {
  const { object, state, data, rowActions, infiniteScroll } = props;
  const containerWidth = useContainerWidth(scrollRef);
  const renderedWidthsRef = useRef<Map<string, number>>(new Map());
  const held = useHeldRows(object.id, data);

  const table = useRecordGridTable({
    object,
    state,
    data: held.data,
    selectable: props.selectable,
    infiniteScroll: Boolean(infiniteScroll),
    renderedWidths: renderedWidthsRef,
  });
  const rows = table.getRowModel().rows;
  const isLoading = Boolean(data.isLoading || (data.mode === "cursor" && data.isLoadingMore));
  const leafColumns = table.getVisibleLeafColumns();
  const hasRowActions = Boolean(rowActions?.length);
  const rowMenu = useRowMenu(object, rowActions, rows);
  useRememberRecordSequence(object, rows);
  useGridScrollRestoration(scrollRef, object, rows.length, {
    ...state,
    accumulatesRows: Boolean(infiniteScroll),
  });
  const layout = useGridColumnLayout({
    leafColumns,
    resizedWidths: state.columns.widths,
    containerWidth,
    hasRowActions,
    renderedWidthsRef,
  });

  const reorder = useColumnReorder(state, table);
  return {
    table,
    rows,
    isLoading,
    isStale: held.isStale,
    visibleColumnCount: leafColumns.length,
    columnIds: leafColumns.map((column) => column.id),
    hasRowActions,
    rowMenu,
    ...layout,
    ...reorder,
  };
}
